import { poAutomationAnalyticsService } from '@/services/po-automation-analytics.service';
import { Commit } from 'vuex';

// Define types for the module's state
interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface SummaryData {
  totalPOsCreated?: number;
  totalValue?: number;
  successRate?: number;
  averageDurationSeconds?: number;
  // Other potential fields
}

interface ChartData {
  labels: string[];
  datasets: any[];
}

interface HistoryData {
  rows: any[];
  count: number;
}

interface LoadingState {
  summary: boolean;
  chart: boolean;
  history: boolean;
  details: boolean;
}

interface PoAutomationDashboardState {
  dateRange: DateRange;
  summaryData: SummaryData | null;
  chartData: ChartData | null;
  historyData: HistoryData;
  executionDetail: any | null; // For a single execution's details
  loading: LoadingState;
  error: string | null;
}

const state: PoAutomationDashboardState = {
  dateRange: {
    startDate: null,
    endDate: null,
  },
  summaryData: null,
  chartData: null,
  historyData: {
    rows: [],
    count: 0,
  },
  executionDetail: null,
  loading: {
    summary: false,
    chart: false,
    history: false,
    details: false,
  },
  error: null,
};

const mutations = {
  SET_LOADING(state: PoAutomationDashboardState, { key, value }: { key: keyof LoadingState; value: boolean }) {
    state.loading[key] = value;
  },
  SET_DATE_RANGE(state: PoAutomationDashboardState, payload: DateRange) {
    state.dateRange = payload;
  },
  SET_SUMMARY_DATA(state: PoAutomationDashboardState, data: SummaryData) {
    state.summaryData = data;
  },
  SET_CHART_DATA(state: PoAutomationDashboardState, data: ChartData) {
    state.chartData = data;
  },
  SET_HISTORY_DATA(state: PoAutomationDashboardState, data: HistoryData) {
    state.historyData = data;
  },
  SET_EXECUTION_DETAIL(state: PoAutomationDashboardState, data: any) {
    state.executionDetail = data;
  },
  SET_ERROR(state: PoAutomationDashboardState, error: string | null) {
    state.error = error;
  },
};

const actions = {
  async fetchDashboardData({ commit, state }: { commit: Commit; state: PoAutomationDashboardState }, options: any) {
    commit('SET_LOADING', { key: 'summary', value: true });
    commit('SET_LOADING', { key: 'chart', value: true });
    commit('SET_LOADING', { key: 'history', value: true });
    commit('SET_ERROR', null);

    try {
      // Fetch summary and chart data together
      const analyticsPromise = poAutomationAnalyticsService.getAnalytics(
        state.dateRange.startDate || '',
        state.dateRange.endDate || '',
      );

      // Fetch history data
      const historyPromise = poAutomationAnalyticsService.getGenerationHistory(options);

      const [analyticsData, historyData] = await Promise.all([analyticsPromise, historyPromise]);

      // Assuming analyticsData contains both summary and chart info
      // This may need adjustment based on actual API response
      commit('SET_SUMMARY_DATA', analyticsData.summary);
      commit('SET_CHART_DATA', analyticsData.chart);
      commit('SET_HISTORY_DATA', historyData);

    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to fetch dashboard data');
    } finally {
      commit('SET_LOADING', { key: 'summary', value: false });
      commit('SET_LOADING', { key: 'chart', value: false });
      commit('SET_LOADING', { key: 'history', value: false });
    }
  },

  async fetchExecutionDetails({ commit }: { commit: Commit }, executionId: string | number) {
    commit('SET_LOADING', { key: 'details', value: true });
    commit('SET_ERROR', null);
    try {
      const details = await poAutomationAnalyticsService.getExecutionDetails(executionId);
      commit('SET_EXECUTION_DETAIL', details);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Failed to fetch execution details');
    } finally {
      commit('SET_LOADING', { key: 'details', value: false });
    }
  },

  updateDateRange({ commit, dispatch }: { commit: Commit; dispatch: Function }, dateRange: DateRange) {
    commit('SET_DATE_RANGE', dateRange);
    // Optionally trigger a data fetch whenever the date range changes
    // dispatch('fetchDashboardData');
  },
};

const getters = {
  summaryData: (state: PoAutomationDashboardState) => state.summaryData,
  chartData: (state: PoAutomationDashboardState) => state.chartData,
  historyData: (state: PoAutomationDashboardState) => state.historyData,
  executionDetail: (state: PoAutomationDashboardState) => state.executionDetail,
  loading: (state: PoAutomationDashboardState) => state.loading,
  error: (state: PoAutomationDashboardState) => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
